import { ReactElement, useCallback, useMemo } from 'react'
import { Box, Stack } from '@mui/material'
import useResultsSectionOpen from '@editor/pages/Results2/hooks/useResultsSectionOpen'
import { Section } from '@ui2/structure'
import { Tabs as DetailTabs } from '@ui/navigation'
import { useStiffeningStore } from '@editorStores'
import { useElementLabel, useResultsSelection } from '@editorHooks'
import useAnchorsOfSegment from '../../hooks/useAnchorsOfSegment'
import useDesignFormNavigationGuard from '../../hooks/useDesignFormNavigationGuard'
import { getWallGuidForStiffeningSegment } from '../../utils'
import Results2DetailCloseButton from '../Results2DetailCloseButton'
import Results2StiffeningCheckResults from '../Results2DetailPane/Results2StiffeningCheckResults'
import type { Results2RelationLink } from '../Results2DetailPane/components/Results2RelationLinksRow'
import { Results2ListItem } from '../Results2ElementList'
import Results2PaneHeader from '../Results2PaneHeader'

interface Props {
  selectedItem?: Results2ListItem
  checks: StiffeningSegmentCheck[]
}

const stiffeningAssemblyLine = (item: Results2ListItem): string | undefined => {
  const name = item.assemblyName?.trim()
  return name || undefined
}

const Results2StiffeningDetailPane = ({ selectedItem, checks }: Props): ReactElement => {
  const getElementLabel = useElementLabel()
  const guardDesignFormNav = useDesignFormNavigationGuard()
  const {
    selectResultsPosition: selectPosition,
    selectResultsAnchor: selectAnchor,
    selectResultsShearAnchor: selectShearAnchor,
  } = useResultsSelection()
  const anchorsOfSegment = useAnchorsOfSegment(selectedItem?.id)
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)
  const designShearFlowSection = useResultsSectionOpen('results2.stiffening.design-shear-flow')
  const checkSection = useResultsSectionOpen('results2.stiffening.check')

  const title =
    selectedItem != null
      ? getElementLabel(selectedItem.id) || selectedItem.label || selectedItem.id
      : 'Keine Wandscheibe ausgewählt'

  const linkedWallGuid = useMemo(
    () =>
      selectedItem != null
        ? getWallGuidForStiffeningSegment(stiffeningIntervals, selectedItem.id)
        : undefined,
    [selectedItem, stiffeningIntervals],
  )

  const handleSelectLinkedWall = useCallback(() => {
    if (!linkedWallGuid) return
    if (guardDesignFormNav()) return
    selectPosition(linkedWallGuid, 'walls', 'wall-assemblies')
  }, [guardDesignFormNav, linkedWallGuid, selectPosition])

  const relationLinks = useMemo((): Results2RelationLink[] => {
    if (selectedItem == null) return []

    const links: Results2RelationLink[] = anchorsOfSegment.map(({ supportGuid, side }) => ({
      label: side === 'rechts' ? 'zum rechten Zuganker' : 'zum linken Zuganker',
      tooltip: 'Zuganker-Nachweis öffnen',
      onClick: () => {
        if (guardDesignFormNav()) return
        selectAnchor(supportGuid)
      },
    }))
    links.push({
      // The shear anchor is chosen per stiffening segment, so it is keyed by
      // this segment's guid.
      label: 'zum Schubanker',
      tooltip: 'Schubanker-Nachweis öffnen',
      onClick: () => {
        if (guardDesignFormNav()) return
        selectShearAnchor(selectedItem.id)
      },
    })
    if (linkedWallGuid) {
      links.push({
        label: 'zur Wand',
        tooltip: 'Wand auswählen',
        onClick: handleSelectLinkedWall,
      })
    }
    return links
  }, [
    anchorsOfSegment,
    guardDesignFormNav,
    handleSelectLinkedWall,
    linkedWallGuid,
    selectAnchor,
    selectShearAnchor,
    selectedItem,
  ])

  const subtitle = useMemo(() => {
    if (!selectedItem) return 'Bitte Segment auswählen'

    return `${selectedItem?.type} · ${selectedItem?.qs} · ${stiffeningAssemblyLine(selectedItem)}`
  }, [selectedItem])

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexShrink: 0, position: 'relative' }}>
        <DetailTabs
          items={[{ id: 'design', label: 'Bemessung' }]}
          value="design"
          size="md"
          kind="folder"
        />
        <Results2DetailCloseButton />
      </Box>
      <Results2PaneHeader
        title={title}
        subtitle={subtitle}
        // assemblyLine={selectedItem != null ? stiffeningAssemblyLine(selectedItem) : undefined}
        relationLinks={selectedItem != null ? relationLinks : []}
      />
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2 }}>
        <Results2StiffeningCheckResults checksOfPosition={checks}>
          {({ design, nachweis }) => (
            <Stack spacing={3} sx={{ py: 1.5 }}>
              <Section
                title="Design Schubfluss"
                collapsible
                hideBorderBottom
                {...designShearFlowSection}
              >
                {design}
              </Section>
              <Section title="Nachweis" collapsible hideBorderBottom {...checkSection}>
                {nachweis}
              </Section>
            </Stack>
          )}
        </Results2StiffeningCheckResults>
      </Box>
    </Box>
  )
}

export default Results2StiffeningDetailPane
